<template>
  <VContainer fluid class="pa-2" data-testid="non-technical-summary">
    <div class="d-flex justify-end mb-4">
      <AutoSaveStatus
        :is-saving="autoSave?.saveInProgress"
        :is-dirty="autoSave?.hasUnsavedChanges"
        :last-saved="autoSave?.lastSaved"
        :save-errors="autoSave?.saveErrors"
      />
    </div>

    <div v-if="summaryData">
      <h3 class="mb-2">{{ t("missionSummary") }}</h3>
      <p class="mb-4">
        {{
          t("missionDescription", {
            context: missionContext ? `(${missionContext})` : "",
            projectName: projectName,
            missionType:
              globalMission === "ELEM"
                ? `${globalMission} ${summaryData?.project?.elementary_missions?.join(", ") || ""}`
                : globalMission,
            address: siteAddress,
          })
        }}
      </p>
      <p class="mb-4">
        {{
          t("siteDescription", {
            surface: totalSurface,
            usage: currentUsage,
          })
        }}
      </p>
      <VRow>
        <VCol>
          <FullWysiwyg
            v-model="form.specificContext"
            label="Contexte spécifique à préciser si besoin (ancienne étude, etc.)"
          />
        </VCol>
      </VRow>

      <VRow v-if="isA100">
        <VCol>
          <h3 class="mb-2">{{ t("remarkableElements") }}</h3>
          <div v-if="hasRemarkableElements && remarkableElements.has_source_elements" class="mb-4">
            <div v-if="buildingsWithSource.length > 0" class="mb-3">
              <h4 class="mb-1">{{ t("buildings") }}:</h4>
              <ul class="ml-6">
                <li v-for="(building, index) in buildingsWithSource" :key="`building-${index}`">
                  {{ building.label }}
                  <ul v-if="building.zones?.length" class="ml-6">
                    <li
                      v-for="(zone, zIndex) in building.zones"
                      :key="`building-${index}-zone-${zIndex}`"
                    >
                      {{ zone.label }}
                      <ul v-if="zone.components?.length" class="ml-6">
                        <li
                          v-for="(component, cIndex) in zone.components"
                          :key="`building-${index}-zone-${zIndex}-comp-${cIndex}`"
                        >
                          {{ component.label }}
                        </li>
                      </ul>
                    </li>
                  </ul>
                  <ul v-if="building.components?.length" class="ml-6">
                    <li
                      v-for="(component, cIndex) in building.components"
                      :key="`building-${index}-comp-${cIndex}`"
                    >
                      {{ component.label }}
                    </li>
                  </ul>
                </li>
              </ul>
            </div>

            <div v-if="outdoorSpacesWithSource.length > 0" class="mb-3">
              <h4 class="mb-1">{{ t("outdoorSpaces") }}:</h4>
              <ul class="ml-6">
                <li v-for="(space, index) in outdoorSpacesWithSource" :key="`outdoor-${index}`">
                  {{ space.label }}
                  <ul v-if="space.zones?.length" class="ml-6">
                    <li
                      v-for="(zone, zIndex) in space.zones"
                      :key="`outdoor-${index}-zone-${zIndex}`"
                    >
                      {{ zone.label }}
                      <ul v-if="zone.components?.length" class="ml-6">
                        <li
                          v-for="(component, cIndex) in zone.components"
                          :key="`outdoor-${index}-zone-${zIndex}-comp-${cIndex}`"
                        >
                          {{ component.label }}
                        </li>
                      </ul>
                    </li>
                  </ul>
                  <ul v-if="space.components?.length" class="ml-6">
                    <li
                      v-for="(component, cIndex) in space.components"
                      :key="`outdoor-${index}-comp-${cIndex}`"
                    >
                      {{ component.label }}
                    </li>
                  </ul>
                </li>
              </ul>
            </div>

            <div v-if="standaloneZonesWithSource.length > 0" class="mb-3">
              <h4 class="mb-1">{{ t("independentZones") }}:</h4>
              <ul class="ml-6">
                <li v-for="(zone, index) in standaloneZonesWithSource" :key="`zone-${index}`">
                  {{ zone.label }}
                </li>
              </ul>
            </div>

            <div v-if="standaloneComponentsWithSource.length > 0" class="mb-3">
              <h4 class="mb-1">{{ t("independentComponents") }}:</h4>
              <ul class="ml-6">
                <li
                  v-for="(component, index) in standaloneComponentsWithSource"
                  :key="`comp-${index}`"
                >
                  {{ component.label }}
                </li>
              </ul>
            </div>
          </div>
          <p v-else class="mb-4">{{ t("noRemarkableElements") }}</p>
        </VCol>
      </VRow>

      <VRow v-if="isA110">
        <VCol cols="12">
          <h3 class="mb-2">{{ t("historicalStudy") }}</h3>
          <div v-if="hasHistoricalActivities" class="mb-4">
            <p v-if="hasSourceTakenIntoAccountActivities" class="mt-2">
              {{
                t("historicalActivitiesSource", {
                  descriptions: sourceActivitiesDescriptions,
                })
              }}
            </p>
            <p v-else class="mt-2">
              {{ t("historicalActivitiesNoRisk") }}
            </p>
          </div>
          <p v-else class="mb-4">{{ t("noHistoricalActivities") }}</p>
        </VCol>
      </VRow>
      <VRow v-if="isNotLeve">
        <VCol v-if="isA100 || isA110" cols="12">
          <h3 class="mb-2">{{ t("contaminationSources") }}</h3>
          <p class="mb-4">{{ contaminationStatusText }}</p>
        </VCol>

        <VCol v-if="showVulnerabilitySection" cols="12">
          <h3 class="mb-2">{{ t("vulnerabilityStudy") }}</h3>
          <div class="mb-4">
            <p class="mb-2">{{ t("vulnerabilityAssignment") }}</p>

            <div v-if="hasUndergroundWater" class="mb-2">
              <strong>{{ t("undergroundWater") }}:</strong>
              {{ getVulnerabilityAndSensitivityText(undergroundWaterVulnerability) }}
              {{ t("vulnerable") }},
              {{ getVulnerabilityAndSensitivityText(undergroundWaterSensitivity) }}
              {{ t("sensitive") }}
              <span v-if="isUndergroundWaterAEP"> ({{ t("aepCapture") }})</span>
            </div>

            <div v-if="hasSurfaceWater" class="mb-2">
              <strong>{{ t("surfaceWater") }}:</strong>
              {{ getVulnerabilityAndSensitivityText(surfaceWaterVulnerability) }}
              {{ t("vulnerable") }},
              {{ getVulnerabilityAndSensitivityText(surfaceWaterSensitivity) }}
              {{ t("sensitive") }}
              <span v-if="isSurfaceWaterAEP"> ({{ t("aepCapture") }})</span>
            </div>

            <div v-if="hasHeritage" class="mb-2">
              <strong>{{ t("environment") }}:</strong>
              {{ getEnvironmentSensitivityText(heritageVulnerability) }}
            </div>
          </div>
        </VCol>
      </VRow>

      <VRow v-if="isA100">
        <VCol cols="12">
          <h3 class="mb-2">{{ t("securityMeasures") }}:</h3>
          <p v-if="!hasSecurityMeasures" class="mb-4">-</p>
          <ul v-else class="ml-6 mb-4">
            <li v-for="measure in uniqueSecurityMeasures" :key="measure">
              {{ measure }}
            </li>
          </ul>
        </VCol>
      </VRow>
      <VRow>
        <VCol>
          <h3 class="mb-2">{{ t("conclusionsRecommendations") }}</h3>
          <ConclusionAndRecommandations non-technical-summary />
        </VCol>
      </VRow>
    </div>

    <VSkeletonLoader v-else-if="loading" type="card" />
    <div v-else>
      <p>{{ t("noData") }}</p>
    </div>
  </VContainer>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useMissionStore } from "@/store/modules/mission";
import { useProjectStore } from "@/store/modules/siteInformation";
import { useSiteStore } from "@/store/modules/site";
import FullWysiwyg from "../site/partials/FullWysiwyg.vue";
import { useI18n } from "vue-i18n";
import ConclusionAndRecommandations from "./ConclusionAndRecommandations.vue";
import AutoSaveStatus from "@/components/base-components/AutoSaveStatus.vue";
import { useFormAutoSave } from "@/composables/useFormAutoSave";
import { VCol } from "vuetify/components";

const { t } = useI18n();
const missionStore = useMissionStore();
const projectStore = useProjectStore();
const siteStore = useSiteStore();

const summaryData = ref(null);
const loading = ref(true);
const form = ref({ specificContext: "" });
const autoSave = ref(null);
const currentMission = computed(() => missionStore.currentMission);

const globalMission = computed(() => summaryData.value?.project?.global_mission || t("undefined"));
const isA100 = computed(() => summaryData.value?.project?.elementary_missions.includes("A100"));
const isA110 = computed(() => summaryData.value?.project?.elementary_missions.includes("A110"));
const isA120 = computed(() => summaryData.value?.project?.elementary_missions.includes("A120"));
const projectName = computed(() => summaryData.value?.project?.project_name || t("undefined"));

const missionContext = computed(() => {
  const context = summaryData.value?.projectInformation?.mission_context;
  return Array.isArray(context) ? context.join(", ") : context || "";
});

const siteAddress = computed(() => {
  const site = summaryData.value?.aosSite;
  if (!site) return t("undefined");

  const parts = [site.address, site.city, site.department].filter(Boolean);
  return parts.join(", ");
});

const totalSurface = computed(() => {
  return summaryData.value?.siteInformation?.total_surface || t("undefined");
});

const currentUsage = computed(() => {
  const usageTypologies = summaryData.value?.siteInformation?.usage_typologies;

  if (Array.isArray(usageTypologies) && usageTypologies.length > 0) {
    let usage;
    if (usageTypologies.length === 2) {
      usage = usageTypologies.join(" et ");
    } else {
      usage = usageTypologies.join(", ");
    }
    return cleanHtml(usage) || t("undefined");
  }

  return t("undefined");
});

const remarkableElements = computed(() => {
  return summaryData.value?.remarquableElements || {};
});

const hasRemarkableElements = computed(() => {
  const elements = remarkableElements.value;
  return !!(
    elements.buildings?.length > 0 ||
    elements.outdoor_spaces?.length > 0 ||
    elements.zones?.length > 0 ||
    elements.components?.length > 0
  );
});

const hasSourceTag = (element) => {
  return element.tags_type?.includes("Source") ?? false;
};
const isFromCartographyOrTestimony = (element) => {
  return element.historical_research_id == null && element.referenced_documents_id == null;
};

const rntTreeElements = computed(() => remarkableElements.value.rnt_tree_elements || []);

const buildingsWithSource = computed(() => {
  const buildingLabels = new Set((remarkableElements.value.buildings || []).map((b) => b.label));
  return rntTreeElements.value.filter((el) => buildingLabels.has(el.label));
});

const outdoorSpacesWithSource = computed(() => {
  const outdoorSpaceLabels = new Set(
    (remarkableElements.value.outdoor_spaces || []).map((o) => o.label),
  );
  return rntTreeElements.value.filter((el) => outdoorSpaceLabels.has(el.label));
});

const standaloneZonesWithSource = computed(() => {
  const treeLabels = new Set(rntTreeElements.value.map((el) => el.label));
  return (
    remarkableElements.value.zones?.filter(
      (zone) =>
        hasSourceTag(zone) &&
        isFromCartographyOrTestimony(zone) &&
        !treeLabels.has(zone.location_inside_building),
    ) || []
  );
});

const standaloneComponentsWithSource = computed(() => {
  const treeLabels = new Set(rntTreeElements.value.map((el) => el.label));
  return (
    remarkableElements.value.components?.filter(
      (component) =>
        hasSourceTag(component) &&
        isFromCartographyOrTestimony(component) &&
        !treeLabels.has(component.location_inside_building),
    ) || []
  );
});

const historicalActivities = computed(() => {
  return summaryData.value?.historicalElements?.activities_with_fusionned || [];
});

const hasHistoricalActivities = computed(() => {
  return historicalActivities.value.length > 0;
});

const hasSourceTakenIntoAccountActivities = computed(() => {
  return summaryData.value?.historicalElements?.has_source_taken_into_account_activities ?? false;
});

const sourceActivitiesDescriptions = computed(() => {
  return (
    summaryData.value?.historicalElements?.source_activities_taken_into_account_descriptions || []
  ).join(", ");
});

const isNotLeve = computed(() => {
  return globalMission.value !== "LEVE";
});

const showVulnerabilitySection = computed(() => {
  return isA120.value && summaryData.value?.vulnerabilities;
});

const contaminationStatusText = computed(() => {
  const historical = summaryData.value?.historicalElements;
  const remarquable = summaryData.value?.remarquableElements;
  if (!historical?.has_source_elements && !remarquable?.has_source_elements) {
    return t("contaminationStatus.none");
  }
  if (historical?.has_sources_taken_into_account || remarquable?.has_sources_taken_into_account) {
    return t("contaminationStatus.present");
  }
  return t("contaminationStatus.negligible");
});

const vulnerabilities = computed(() => {
  return summaryData.value?.vulnerabilities || {};
});

const hasUndergroundWater = computed(() => {
  return (
    vulnerabilities.value?.underground_water_information &&
    vulnerabilities.value?.underground_water_usage
  );
});

const undergroundWaterVulnerability = computed(() => {
  return vulnerabilities.value?.underground_water_information?.vulnerability_level;
});

const undergroundWaterSensitivity = computed(() => {
  return vulnerabilities.value?.underground_water_usage?.sensitivity;
});

const isUndergroundWaterAEP = computed(() => {
  const usage = vulnerabilities.value?.underground_water_usage;
  return usage?.is_inside_capture_zone || usage?.is_aep_usage;
});

const hasSurfaceWater = computed(() => {
  return (
    vulnerabilities.value?.surface_water_information && vulnerabilities.value?.surface_water_usage
  );
});

const surfaceWaterVulnerability = computed(() => {
  return vulnerabilities.value?.surface_water_information?.vulnerability_level;
});

const surfaceWaterSensitivity = computed(() => {
  return vulnerabilities.value?.surface_water_usage?.sensitivity;
});

const isSurfaceWaterAEP = computed(() => {
  return vulnerabilities.value?.surface_water_usage?.is_aep_usage;
});

const hasHeritage = computed(() => {
  return vulnerabilities.value?.heritage_information;
});

const heritageVulnerability = computed(() => {
  return vulnerabilities.value?.heritage_information?.vulnerability_level;
});

const uniqueSecurityMeasures = ref([]);

const hasSecurityMeasures = computed(() => {
  return uniqueSecurityMeasures.value.length > 0;
});

function cleanHtml(html) {
  if (html.length > 100000) {
    throw new Error("Input too long");
  }
  return html.replace(/<[^>]{0,100}>/g, "");
}

const getVulnerabilityAndSensitivityText = (level) => {
  switch (level) {
    case "Forte":
      return t("highly");
    case "Moyenne":
      return t("moderately");
    case "Faible":
      return t("weakly");
    default:
      return t("undefined");
  }
};

const getEnvironmentSensitivityText = (level) => {
  switch (level) {
    case "Forte":
      return t("environmentSensitivity.high");
    case "Moyenne":
      return t("environmentSensitivity.moderate");
    case "Faible":
      return t("environmentSensitivity.low");
    default:
      return t("undefined");
  }
};

const initializeAutoSave = () => {
  autoSave.value = useFormAutoSave(projectStore, form, {
    saveMethod: "partialUpdateSiteInformation",
    validator: async () => ({ isValid: true, message: "" }),
    transformer: (data) => ({
      siteInformation: {
        ...projectStore.getCurrentSiteInformation,
        specificContext: data.specificContext,
      },
    }),
  });
  autoSave.value.initializeTracking(form.value);
};

const loadSecurityMeasures = async () => {
  try {
    const site = await siteStore.fetchCurrentSiteWithImmediateDangers();
    uniqueSecurityMeasures.value = [
      ...new Set(
        site.immediateDangers
          .map((immediateDanger) => {
            return immediateDanger.securityMeasures;
          })
          .flat(),
      ),
    ];
  } catch (error) {
    console.error("Error loading security measures:", error);
    uniqueSecurityMeasures.value = [];
  }
};

const loadData = async () => {
  try {
    loading.value = true;
    const mission = currentMission.value;

    if (!mission?.uuid) {
      console.error("No mission UUID available");
      return;
    }

    const response = await missionStore.fetchAndInsertNonTechnicalSummary({
      uuid: mission.uuid,
    });

    summaryData.value = response;
    console.log("Summary data loaded:", response);
  } catch (error) {
    console.error("Error loading non-technical summary:", error);
    summaryData.value = {};
  } finally {
    loading.value = false;
  }
};

onMounted(async () => {
  await loadData();
  await loadSecurityMeasures();
  await projectStore.fetchCurrentSiteInformation();

  // Initialize form with loaded data
  form.value.specificContext = projectStore.getCurrentSiteInformation?.specificContext || "";

  // Initialize autosave tracking
  initializeAutoSave();
});
</script>

<style lang="scss" scoped>
h3 {
  letter-spacing: 0;
  font-weight: 700;
  line-height: 1.125rem;
  font-size: 1rem;
}

p {
  font-weight: 400;
  letter-spacing: 0;
  line-height: 100%;
  font-size: 0.875rem;
}
</style>

<i18n>
{
  "en": {
    "loading": "Loading...",
    "noData": "No data available",
    "missionSummary": "Mission Summary",
    "remarkableElements": "Remarkable Elements",
    "historicalStudy": "Historical Study",
    "contaminationSources": "Potential Contamination Sources",
    "vulnerabilityStudy": "Vulnerability Assessment",
    "securityMeasures": "Security Measures",
    "conclusionsRecommendations": "Conclusions and Recommendations",
    "buildings": "Buildings",
    "outdoorSpaces": "Outdoor Spaces",
    "independentZones": "Independent Zones",
    "independentComponents": "Independent Components",
    "undergroundWater": "Underground water",
    "surfaceWater": "Surface water",
    "environment": "Environment",
    "aepCapture": "AEP Capture",
    "highly": "highly",
    "moderately": "moderately",
    "weakly": "weakly",
    "vulnerable": "vulnerable",
    "sensitive": "sensitive",
    "environmentSensitivity": {
      "high": "highly vulnerable environment",
      "moderate": "moderately vulnerable environment",
      "low": "low vulnerable environment"
    },
    "missionDescription": "In order to meet your need {context}, the mission \"{projectName}\" ({missionType}) was carried out by SOCOTEC Environnement, on the site located at the following address: {address}.",
    "siteDescription": "The site represents an area of {surface} m² and is used for {usage} usage.",
    "noRemarkableElements": "During the site visit, no remarkable elements were identified.",
    "historicalActivitiesFound": "The historical study revealed the following past activities:",
    "historicalActivitiesSource": "The historical study revealed former potentially polluting activities on the site ({descriptions}).",
    "historicalActivitiesNoRisk": "Among the former activities carried out on the site identified during the historical study, none presented a pollution risk.",
    "noHistoricalActivities": "The historical study did not reveal the existence of other activities in the past other than the current activity.",
    "contaminationStatus": {
      "none": "This study revealed the absence of potential contamination sources.",
      "negligible": "This study revealed the presence of potential contamination sources. However, the associated risk is considered negligible.",
      "present": "This study revealed the presence of potential contamination sources."
    },
    "vulnerabilityAssignment": "The vulnerability study allowed to assign:",
    "undefined": "undefined"
  },
  "fr": {
    "loading": "Chargement...",
    "noData": "Aucune donnée disponible",
    "missionSummary": "Résumé de Mission",
    "remarkableElements": "Éléments Remarquables",
    "historicalStudy": "Étude Historique",
    "contaminationSources": "Sources Potentielles de Contamination",
    "vulnerabilityStudy": "Étude de Vulnérabilité",
    "securityMeasures": "Mesures de Sécurité",
    "conclusionsRecommendations": "Conclusions et Recommandations",
    "buildings": "Bâtiments",
    "outdoorSpaces": "Espaces Extérieurs",
    "independentZones": "Zones Indépendantes",
    "independentComponents": "Composants Indépendants",
    "undergroundWater": "Eaux souterraines",
    "surfaceWater": "Eaux superficielles",
    "environment": "Environnement",
    "aepCapture": "Captage AEP",
    "highly": "fortement",
    "moderately": "moyennement",
    "weakly": "faiblement",
    "vulnerable": "vulnérables",
    "sensitive": "sensibles",
    "environmentSensitivity": {
      "high": "fortement vulnérable de l'environnement",
      "moderate": "moyennement vulnérable de l'environnement",
      "low": "peu vulnérable de l'environnement"
    },
    "missionDescription": "Afin de répondre à votre besoin {context}, la mission \"{projectName}\" ({missionType}) a été réalisée par SOCOTEC Environnement, sur le site localisé à l'adresse suivante : {address}.",
    "siteDescription": "Le site représente une surface de {surface} m² et est utilisé pour un usage {usage}.",
    "noRemarkableElements": "Lors de la visite de site, aucun élément remarquable n'a été identifié.",
    "historicalActivitiesFound": "L'étude historique a permis de mettre en évidence les activités passées suivantes :",
    "historicalActivitiesSource": "L'étude historique a mis en évidence d'anciennes activités potentiellement polluantes au droit du site ({descriptions}).",
    "historicalActivitiesNoRisk": "Parmi les anciennes activités exercées au droit du site, identifiées lors de l'étude historique, aucune n'a présenté de risque de pollution.",
    "noHistoricalActivities": "L'étude historique n'a pas mis en évidence l'existence d'autres activités par le passé autre que l'activité actuelle.",
    "contaminationStatus": {
      "none": "Cette étude a mis en évidence l'absence de source potentielle de contamination.",
      "negligible": "Cette étude a mis en évidence la présence de source potentielle de contamination. Cependant, le risque associé est jugé négligeable.",
      "present": "Cette étude a mis en évidence la présence de source potentielle de contamination."
    },
    "vulnerabilityAssignment": "L'étude de vulnérabilité a permis d'attribuer :",
    "undefined": "Non défini"
  }
}
</i18n>
